import { useCallback, useEffect, useState } from 'react';

import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { useClerk, useSignUp } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

interface AcceptInvitationFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

export const useAcceptInvitation = () => {
  const { signUp } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<'loading' | 'form' | 'error' | 'migrated'>('loading');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  const ticket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status');

  const acceptInvitation = useCallback(async () => {
    if (!clerk.loaded) return;

    if (!ticket) {
      setStep('error');
      return;
    }

    if (clerkStatus === 'sign_in') {
      setStep('migrated');
      return;
    }

    try {
      const { error: ticketError } = await signUp.ticket({ ticket });

      if (ticketError) {
        setError(mapClerkError(ticketError, t));
        setStep('error');
        return;
      }

      setEmail(signUp.emailAddress ?? null);
      setStep('form');
    } catch (err) {
      setError(mapClerkError(err, t));
      setStep('error');
    }
  }, [clerk.loaded, ticket, signUp, t]);

  useEffect(() => {
    void acceptInvitation();
  }, [acceptInvitation]);

  const handleSubmit = useCallback(
    async (data: AcceptInvitationFormValues) => {
      setLoading(true);
      setError(null);

      try {
        // Update user profile (firstName, lastName)
        const { error: updateError } = await signUp.update({
          firstName: data.firstName,
          lastName: data.lastName
        });

        if (updateError) {
          setError(mapClerkError(updateError, t));
          return false;
        }

        // Set password if the sign-up still requires it
        if (signUp.status !== 'complete') {
          const emailAddress = signUp.emailAddress;
          if (!emailAddress) {
            setError({ type: 'general', message: t('error.acceptInvitation.unexpectedStatus') });
            return false;
          }

          const { error: pwError } = await signUp.password({
            password: data.password,
            emailAddress
          });

          if (pwError) {
            setError(mapClerkError(pwError, t));
            return false;
          }
        }

        // Complete the sign-up
        if (signUp.status === 'complete') {
          await signUp.finalize({ navigate: createFinalizeNavigate(navigate) });
          toast.success(t('success.auth.acceptInvitation'));
          return true;
        }

        setError({ type: 'general', message: t('error.acceptInvitation.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signUp, navigate, t]
  );

  return {
    step,
    error,
    loading,
    isLoaded: clerk.loaded,
    email,
    hasTicket: !!ticket,
    handleSubmit
  };
};
